import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2 } from 'lucide-react';
import { paymentAgentsAPI, fmtError } from '@/lib/api';

const ID_TYPES = [
  { value: 'passport', label: 'International Passport' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'national_id', label: 'National ID' },
];

const METHOD_OPTS = ['Bank Transfer', 'Cash', 'E-Wallet (USSD)', 'PayPal', 'Mobile Money', 'Skrill', 'Wise', 'Crypto'];

/**
 * Public form (no auth required) — applicants submit their identity + a photo of their ID.
 * We base64-encode the file client-side and store it directly on the application doc.
 * Simple, no S3 dependency, easy admin preview.
 */
export default function PaymentAgentsApply() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', country: '', contact_url: '',
    methods: [], why: '',
    id_type: 'passport', id_number: '', id_image_b64: '',
  });
  const [filePreview, setFilePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const toggleMethod = (m) => {
    setForm(f => ({
      ...f,
      methods: f.methods.includes(m) ? f.methods.filter(x => x !== m) : [...f.methods, m],
    }));
  };

  const onFile = (e) => {
    setErr('');
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) { setErr('Please upload an image file (jpg / png).'); return; }
    if (f.size > 3 * 1024 * 1024) { setErr('Photo too large — keep it under 3 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result || '').split(',')[1] || '';
      setForm(prev => ({ ...prev, id_image_b64: b64 }));
      setFilePreview(String(reader.result || ''));
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.methods.length === 0) { setErr('Pick at least one payment method you support.'); return; }
    if (!form.id_image_b64) { setErr('Please upload a clear photo of your ID.'); return; }
    setSubmitting(true);
    try {
      await paymentAgentsAPI.apply({
        name: form.name, email: form.email, country: form.country,
        contact_url: form.contact_url, methods: form.methods, why: form.why,
        id_type: form.id_type, id_number: form.id_number, id_image_b64: form.id_image_b64,
      });
      setDone(true);
    } catch (e) { setErr(fmtError(e)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center">
        <CheckCircle2 size={48} className="text-[#00ff66] mx-auto" />
        <h1 className="font-display text-3xl mt-4">Application received.</h1>
        <p className="text-sm text-zinc-400 mt-2">
          Our team will review your details and ID document within 1–3 business days.
          We'll email <span className="text-white">{form.email}</span> as soon as you're approved.
        </p>
        <div className="mt-6">
          <Link to="/payment-agents" className="btn-ghost btn-sm"><ArrowLeft size={14} /> Back to agents</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-10">
      <Link to="/payment-agents" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="back-to-agents">
        <ArrowLeft size={14} /> Payment Agents
      </Link>
      <h1 className="font-display text-3xl mt-3">Apply to become a Payment Agent</h1>
      <p className="text-sm text-zinc-400 mt-2 max-w-xl">
        Earn a commission on every trader you help onboard. Submit the form below and we'll review your application.
        All applicants must verify their identity — pick one document type and upload a clear photo.
      </p>

      <form className="mt-6 space-y-4 card-tech rounded-lg p-6" onSubmit={submit} data-testid="apply-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="label-xs mb-1">Full name</div>
            <input className="input-tech" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
          </div>
          <div>
            <div className="label-xs mb-1">Email</div>
            <input className="input-tech" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
          </div>
          <div>
            <div className="label-xs mb-1">Country / region</div>
            <input className="input-tech" required value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="apply-country" />
          </div>
          <div>
            <div className="label-xs mb-1">Contact URL (WhatsApp / Telegram / link)</div>
            <input className="input-tech" required placeholder="https://wa.me/..." value={form.contact_url} onChange={e => setForm({ ...form, contact_url: e.target.value })} data-testid="apply-contact" />
          </div>
        </div>

        <div>
          <div className="label-xs mb-2">Payment methods you can offer</div>
          <div className="flex flex-wrap gap-2">
            {METHOD_OPTS.map(m => (
              <button
                type="button" key={m}
                onClick={() => toggleMethod(m)}
                className={`text-xs font-mono tracking-wider px-3 py-1.5 rounded-full border transition ${
                  form.methods.includes(m)
                    ? 'border-[#00ff66] bg-[#00ff66]/15 text-[#00ff66]'
                    : 'border-white/10 text-zinc-400 hover:border-white/30'
                }`}
                data-testid={`apply-method-${m.toLowerCase().replace(/\W+/g, '-')}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="label-xs mb-1">Why you want to be an agent (1–2 sentences)</div>
          <textarea className="input-tech" rows={3} value={form.why} onChange={e => setForm({ ...form, why: e.target.value })} data-testid="apply-why" />
        </div>

        <div className="border-t border-white/5 pt-4">
          <div className="label-sm mb-3">Identity verification</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="label-xs mb-1">Document type</div>
              <select className="input-tech" value={form.id_type} onChange={e => setForm({ ...form, id_type: e.target.value })} data-testid="apply-id-type">
                {ID_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <div className="label-xs mb-1">Document number</div>
              <input className="input-tech" required value={form.id_number} onChange={e => setForm({ ...form, id_number: e.target.value })} data-testid="apply-id-number" />
            </div>
          </div>
          <div className="mt-3">
            <div className="label-xs mb-1">Upload a clear photo of your document (jpg / png, max 3 MB)</div>
            <label className="flex items-center gap-3 border border-dashed border-white/15 rounded-lg p-4 cursor-pointer hover:border-[#00ff66]/40" data-testid="apply-id-upload">
              <Upload size={16} className="text-[#00ff66]" />
              <span className="text-sm text-zinc-400">
                {form.id_image_b64 ? 'File loaded — click to change' : 'Click to choose a photo'}
              </span>
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            {filePreview && (
              <img src={filePreview} alt="ID preview" className="mt-3 max-h-48 rounded-md border border-white/10" />
            )}
          </div>
        </div>

        {err && <div className="text-sm text-[#ff3366]" data-testid="apply-err">{err}</div>}

        <div className="flex gap-2">
          <button className="btn-primary" disabled={submitting} data-testid="apply-submit">
            {submitting ? 'Submitting…' : 'Submit application'}
          </button>
          <button type="button" className="btn-ghost" onClick={() => nav('/payment-agents')}>Cancel</button>
        </div>
        <div className="text-xs text-zinc-500">
          Your ID is encrypted in transit and only viewable by approved admins. We never share it with traders.
        </div>
      </form>
    </div>
  );
}
